import {
    SortBy,
    type Deck,
    type S3Location,
} from "@amzn/ads-magneto-typescript-client";
import type { GeneratedInsight } from "@amzn/d16-g-client-deck-builder-typescript-client";
import { Status } from "@amzn/d16-g-client-deck-builder-typescript-client";
import type { AsyncState, UseAsyncActions } from "@react-hookz/web";
import { useAsync } from "@react-hookz/web";
import { useMemo } from "react";
import * as R from "remeda";

import { getCdbApiClient } from "../../api/cdbApi/cdbApi";
import { useMagnetoApi } from "../../api/magnetoApi/magnetoApi";
import { authStore } from "../../store/auth";
import { FeatureFlagsStore } from "../../store/featureFlags";
import { yourInsightFilterStore } from "../../store/yourInsightsFilter";
import type { DownloadAsset } from "../../types/common";
import { YourInsightFilterContentSubType } from "../../types/filters/yourInsights";
import { getAdvertiserId } from "../../utils/permission.utils";
import { useAsyncErrorStatusNotification } from "../notifications/useAsyncErrorStatusNotification";

export function magnetoDeckToGeneratedInsights(deck: Deck): GeneratedInsight {
    return {
        advertiser: "n/a",
        identifier: deck.id,
        advertiserId: "n/a",
        insightType: "WB",
        status: "SUCCESS",
        step: "VISUALIZATION",
        title: deck.name,
        updateTimestamp: deck.lastModifiedDate,
        metadata: {
            globalWorkbench: true,
            description: deck.description ?? "",
            sourceSummaries: R.unique(
                (deck.sources ?? []).map((s) => {
                    if (s.origin && s.origin.subType) {
                        return s.origin.subType;
                    }
                    if (s.origin && s.origin.type) {
                        return s.origin.type;
                    }
                    if (s.origin && s.origin.clientId) {
                        return s.origin.clientId;
                    }
                    return "Source Unknown";
                }),
            ),
        },
    };
}

export function magnetoS3LocationToDownloadAsset(
    asset: S3Location | undefined,
): DownloadAsset {
    if (!asset) {
        throw new Error("No asset found");
    }
    return {
        content: {
            bucket: asset.bucket,
            key: asset.key,
        },
        downloadUrl: asset.s3signedUrl,
        asyncStatus: "success",
        contentId: "",
        contentVersion: "",
    };
}

export function useGeneratedInsightsAction(): [
    AsyncState<GeneratedInsight[]>,
    UseAsyncActions<GeneratedInsight[]>,
] {
    const magnetoApi = useMagnetoApi();
    const generatedInsightType =
        yourInsightFilterStore.getState().contentSubType;

    // Handle Insight Retrieval
    const [insightsState, insightsAction] = useAsync(async () => {
        const generatedInsightType =
            yourInsightFilterStore.getState().contentSubType;

        const allInsights: GeneratedInsight[] = [];

        if (
            FeatureFlagsStore.getState().globalWorkbench === true &&
            generatedInsightType ===
                YourInsightFilterContentSubType.WORKBENCH_INSIGHTS
        ) {
            if (magnetoApi) {
                const { data } = await magnetoApi.getDecks({
                    workbenchScope: getAdvertiserId(),
                    sortBy: SortBy.LAST_GENERATED,
                    pageSize: 100,
                });

                allInsights.push(
                    ...(data || []).map((d) =>
                        magnetoDeckToGeneratedInsights(d),
                    ),
                );
            }
        }

        const requesterAlias = authStore.getState().username;
        const { data } = await (
            await getCdbApiClient()
        ).getGeneratedInsights({
            requesterAlias,
            advertiserId: getAdvertiserId(),
            generatedInsightType,
        });
        // In progress insights are shown in snack bar
        const notInProgressInsights: GeneratedInsight[] = (data ?? []).filter(
            (ins) => ins.status !== Status.IN_PROGRESS,
        );
        allInsights.push(...notInProgressInsights);
        allInsights.sort((a, b) => {
            const aTimestamp = a.updateTimestamp;
            const bTimestamp = b.updateTimestamp;

            // Handle missing timestamps - items without timestamps go to end
            if (!aTimestamp && !bTimestamp) return 0;
            if (!aTimestamp) return 1;
            if (!bTimestamp) return -1;

            const aDate = new Date(aTimestamp);
            const bDate = new Date(bTimestamp);

            // Handle invalid dates - invalid dates go to end
            const aIsValid = !isNaN(aDate.getTime());
            const bIsValid = !isNaN(bDate.getTime());

            if (!aIsValid && !bIsValid) return 0;
            if (!aIsValid) return 1;
            if (!bIsValid) return -1;

            return bDate.getTime() - aDate.getTime();
        });
        return allInsights;
    }, []);

    // useAsync doesn't recreate action when dependencies change, so we wrap it with a new reference
    const insightsActionWithDependencyTracking = useMemo(
        () => ({
            ...insightsAction,
            _nonce: Date.now(),
        }),
        [insightsAction, magnetoApi, generatedInsightType],
    );

    useAsyncErrorStatusNotification(insightsState);

    return [insightsState, insightsActionWithDependencyTracking];
}
